import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, CreditCardIcon } from 'lucide-react';
function FixedCountdown() {
  const [timeLeft, setTimeLeft] = useState({
    d: 0,
    h: 0,
    m: 0,
    s: 0,
    expired: false
  });
  useEffect(() => {
    // Target: April 28, 2026 23:59:59 UTC-3.
    // UTC-3 means 02:59:59 UTC on April 29
    const targetDate = new Date('2026-04-29T02:59:59Z').getTime();
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate - now;
      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({
          d: 0,
          h: 0,
          m: 0,
          s: 0,
          expired: true
        });
        return;
      }
      setTimeLeft({
        d: Math.floor(distance / (1000 * 60 * 60 * 24)),
        h: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        m: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
        s: Math.floor(distance % (1000 * 60) / 1000),
        expired: false
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  if (timeLeft.expired) {
    return <span className="inline-flex items-center bg-dark px-3 py-1.5 rounded-lg border border-pain/30 font-mono text-pain text-xl md:text-2xl font-black" data-id="element-132">
        OFERTA FINALIZADA
      </span>;
  }
  return <span className="inline-flex items-center bg-dark px-3 py-1.5 rounded-lg border border-neon/30 font-mono text-neon text-xl md:text-2xl font-black" data-id="element-133">
      {timeLeft.d}d {String(timeLeft.h).padStart(2, '0')}h{' '}
      {String(timeLeft.m).padStart(2, '0')}m{' '}
      {String(timeLeft.s).padStart(2, '0')}s
    </span>;
}
const stackItems = [{
  emoji: '✅',
  name: 'Cómo Eliminar la Fascitis Plantar en 30 Días — Protocolo completo (PDF + Tablas + Fichas)',
  value: '$45.000'
}, {
  emoji: '🎁',
  name: 'Gift Card $6.000 ARS JumpBox',
  value: '$6.000'
}, {
  emoji: '🎁',
  name: 'Ebook "Mentalidad de Acero"',
  value: '$12.000'
}, {
  emoji: '🎁',
  name: 'Guía de Calzado Inteligente',
  value: '$8.000'
}];
export function ProductSection() {
  return <section id="pricing" className="py-6 md:py-10 px-4" data-id="element-134">
      <div className="max-w-3xl mx-auto" data-id="element-135">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-4" data-id="element-136">
          <h2 className="text-xl md:text-2xl font-black uppercase text-white mb-2" data-id="element-137">
            Existe una salida.{' '}
            <span className="text-neon" data-id="element-138">Y no es otra plantilla.</span>
          </h2>
          <p className="text-base md:text-lg text-white max-w-2xl mx-auto" data-id="element-139">
            Después de trabajar con +6.000 deportistas argentinos, desarrollamos{' '}
            <span className="text-neon font-bold" data-id="element-140">
              Cómo Eliminar la Fascitis Plantar en 30 Días
            </span>
            : un sistema de 3 fases que ataca la causa real del dolor y te
            devuelve la función de resorte que tu pie perdió.
          </p>
        </motion.div>

        {/* Full offer mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="flex justify-center mb-4" data-id="element-141">
          <video src="https://cdn.magicpatterns.com/uploads/5hX3urGqfWVj7zAc662Qxv/EBOOK%20VIDEO%20.mp4" autoPlay loop muted playsInline className="w-full max-w-md rounded-xl shadow-[0_0_30px_rgba(212,244,22,0.1)]" data-id="element-142" />
        </motion.div>

        {/* Authority Line */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-4 text-center" data-id="element-143">
          <p className="text-neon text-xs md:text-sm font-bold uppercase tracking-widest" data-id="element-144">
            Desarrollado por el equipo JumpBox · +6.000 deportistas tratados ·
            Protocolo validado en Argentina
          </p>
        </motion.div>

        {/* Stack */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border border-dark-border rounded-xl overflow-hidden mb-4" data-id="element-145">
          <div className="bg-neon/10 px-4 py-3 border-b border-dark-border" data-id="element-146">
            <h3 className="text-base md:text-lg font-black uppercase text-center text-white" data-id="element-147">
              Lo que te llevás hoy
            </h3>
          </div>
          <div className="divide-y divide-dark-border" data-id="element-148">
            {stackItems.map((item, i) => <div key={i} className="flex items-center justify-between gap-3 px-4 py-3" data-id="element-149">
                <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-150">
                  <span className="shrink-0" data-id="element-151">{item.emoji}</span>
                  <span className="text-base md:text-lg text-white" data-id="element-152">
                    {item.name}
                  </span>
                </div>
                <span className="text-base font-bold text-white shrink-0" data-id="element-153">
                  {item.value}
                </span>
              </div>)}
          </div>
          <div className="bg-dark-card px-4 py-3 flex items-center justify-between border-t border-dark-border" data-id="element-154">
            <span className="font-bold text-white uppercase text-base" data-id="element-155">
              Valor total:
            </span>
            <span className="text-pain font-black line-through text-lg" data-id="element-156">
              $71.000
            </span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-dark-card border border-dark-border rounded-xl p-5 text-center" data-id="element-157">
          <div className="inline-flex items-center bg-dark border border-neon/30 rounded-full px-4 py-1.5 text-neon text-xs md:text-sm uppercase tracking-widest font-bold mb-3" data-id="element-158">
            +847 deportistas ya eliminaron su fascitis plantar
          </div>

          <p className="text-neon font-bold text-base md:text-lg mb-2" data-id="element-159">
            ⚡ Precio de lanzamiento hasta el lunes 28/04 — Después sube a
            $29.900
          </p>

          <FixedCountdown data-id="element-160" />

          <p className="text-white uppercase tracking-widest text-base mt-3 mb-1" data-id="element-161">
            Precio de lanzamiento:
          </p>
          <div className="text-4xl md:text-5xl font-black text-neon mb-1" data-id="element-162">
            $19.900 ARS
          </div>
          <p className="text-white text-base mb-4" data-id="element-163">
            Pago único · Acceso de por vida
          </p>

          <motion.a href="https://jumpbox.ar/cart/51184272441639:1" onClick={() => (window as any).fbq?.('track', 'AddToCart')} animate={{
          scale: [1, 1.03, 1]
        }} transition={{
          repeat: Infinity,
          duration: 2
        }} className="inline-flex items-center justify-center gap-2 w-full py-4 bg-neon text-black font-heading font-black rounded-xl text-base md:text-lg uppercase tracking-wide hover:bg-neon-hover transition-colors shadow-[0_0_25px_rgba(212,244,22,0.2)]" data-id="element-164">
            QUIERO PISAR SIN DOLOR — $19.900 ARS{' '}
            <ArrowRightIcon className="w-5 h-5" data-id="element-165" />
          </motion.a>

          <div className="mt-4 flex flex-col items-center justify-center gap-2 bg-dark border border-neon/20 rounded-lg p-3" data-id="element-166">
            <ShieldCheckIcon className="w-6 h-6 text-neon" data-id="element-167" />
            <p className="text-neon text-xs md:text-sm font-bold uppercase tracking-wide" data-id="element-168">
              7 días de garantía · Si no funciona, te devolvemos el dinero
            </p>
          </div>

          <p className="text-white/60 text-xs mt-4" data-id="element-169">
            Precio de lanzamiento. Cuando tengamos 500 ventas, sube. Puede ser
            mañana.
          </p>

          <div className="mt-4 flex flex-col items-center justify-center gap-3" data-id="element-170">
            <div className="flex items-center justify-center gap-2 text-white text-base" data-id="element-171">
              <img src="https://cdn.magicpatterns.com/uploads/cLjsfktCqCq1JWEeQZa4oj/pasted-image.png" alt="Mercado Pago" className="w-6 h-6 rounded-full" data-id="element-172" />
              <span data-id="element-173">Pago seguro con Mercado Pago</span>
            </div>
            <div className="flex items-center justify-center gap-3 opacity-60 grayscale" data-id="element-174">
              <CreditCardIcon className="w-6 h-6" data-id="element-175" />
              <span className="font-bold text-xs md:text-sm" data-id="element-176">VISA</span>
              <span className="font-bold text-xs md:text-sm" data-id="element-177">MASTERCARD</span>
              <span className="font-bold text-xs md:text-sm" data-id="element-178">AMEX</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}